<script lang="ts" setup>
import { computed } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import MemberActivityCard from '@/components/home/member-activity-card.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { formatDateTime } from '@/utils/formatters.js';
import { getMemberActivityKey } from '@/utils/memberActivity';
import { STATES, useMemberActivity, useUserInfo } from '@/utils/swr';

const layoutVariant = 'next';

const maxVisible = 8;

const { data: userInfo, state: userInfoState } = useUserInfo();
const { data: activitiesResponse, state } = useMemberActivity(maxVisible);
const activities = computed(() => activitiesResponse.value?.activities ?? []);

const isLoading = computed(
  () =>
    userInfoState.value === STATES.PENDING || state.value === STATES.PENDING,
);

const fewActivities = computed(() => activities.value.length < 5);
const noActivities = computed(() => activities.value.length === 0);

const { requestId, setRef } = useHomeSectionViewTracking(
  'member_activities_view',
  () => !isLoading.value,
  () => ({
    document_count: activities.value.length,
    layout_variant: layoutVariant,
  }),
);
</script>

<template>
  <div class="member-activities aconnect-home-component" :ref="setRef">
    <div class="title">
      <DgrIcon
        name="group-mark"
        :keep-fill="false"
        class="aconnect-home-icon animated icon"
      />
      <div class="c-title-bold--xm">メンバーの活動状況</div>
    </div>
    <div class="c-text-regular--s subtitle">
      チームの「気になる」をまとめてチェック。メンバーのマークやコメントなど、最新の動きが届きます。
    </div>
    <div class="loading" v-if="isLoading">
      <DgrLoading />
    </div>
    <template v-else>
      <div class="activities">
        <MemberActivityCard
          v-for="activity in activities"
          :key="getMemberActivityKey(activity)"
          :activity="activity"
          :tracking-request-id="requestId"
        />
      </div>
      <div v-if="activities.length < maxVisible" class="remaining">
        <router-link
          v-if="activities.length === maxVisible - 1"
          :to="{ name: 'industryNews' }"
          class="c-outlineBtn c-outlineBtn--small c-outlineBtn--auto follow-lists"
        >
          気になる記事にマークしてみる
        </router-link>
        <div v-else class="empty">
          <div class="content" :class="{ 'no-activities': noActivities }">
            <div v-if="fewActivities" class="icon">
              <DgrIcon name="group-mark" :keep-fill="false" />
            </div>
            <div class="c-title-medium--m">
              メンバーの動きが、ここに集まります
            </div>
            <template v-if="noActivities">
              <div class="c-text-regular--s">
                誰かがニュースや論文にマークやコメントをすると、こんなふうに届きます。
              </div>
              <div class="activity-example">
                <div class="c-title-bold--s">表示イメージ</div>
                <div class="activity">
                  <img :src="userInfo?.image_url" class="profile-image" />
                  <div class="content">
                    <div class="c-text-regular--m">
                      <span class="c-title-medium--m">田中太郎</span>さんが<span
                        class="c-title-bold--m action-label"
                        >コメント</span
                      >しました。
                    </div>
                    <div class="c-text-regular--s">
                      {{ formatDateTime('2026-08-27 13:21') }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="fewActivities" class="c-text-regular--s">
              誰かがニュースや論文にマークやコメントをすると、ここに届きます。
            </div>
            <div v-if="fewActivities" class="c-text-regular--s">
              マークとは、気になる情報の
              <DgrIcon name="check-circle" size="small" :keep-fill="false" />
              を押して保存すること。マークするほど、AIのおすすめ精度も上がります。
            </div>
            <div class="c-text-regular--s">
              まずはあなたもマークしてみませんか？
            </div>
            <router-link
              :to="{ name: 'followLists' }"
              class="c-outlineBtn c-outlineBtn--small c-outlineBtn--auto follow-lists"
            >
              気になる記事にマークしてみる
            </router-link>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.member-activities {
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  box-sizing: border-box;
  background: #ffffff;

  .title {
    display: flex;
    align-items: center;
    gap: 4px;

    .icon {
      color: $color-green600;
    }
  }

  .subtitle {
    margin-top: 12px;
    margin-bottom: 16px;
    color: $color-gray800;
  }

  .loading {
    display: flex;
    justify-content: center;
  }

  .activities {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .remaining {
    display: flex;
    flex: 1;
    margin-top: 16px;

    .empty {
      display: flex;
      justify-content: center;
      flex: 1;
      border: 1px dashed $color-gray400;
      border-radius: 4px;

      > .content {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: auto;
        padding: 12px 24px;

        &.no-activities {
          gap: 16px;

          a {
            margin-top: 16px;
          }
        }

        .c-title-medium--m {
          text-align: center;
        }

        .activity-example {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 24px;

          .c-title-bold--s {
            color: $color-gray800;
          }

          .activity {
            display: flex;
            gap: 8px;
            padding: 8px 16px;
            background: $color-gray200;
            border-radius: 4px;

            &:hover {
              background: $color-gray400;
            }

            .action-label {
              color: $color-p7;
            }

            .profile-image {
              width: 24px;
              height: 24px;
              border-radius: 50%;
            }

            .content {
              display: flex;
              flex-direction: column;
              gap: 4px;
              min-width: 0;
            }
          }
        }

        .icon {
          box-sizing: border-box;
          width: 48px;
          height: 48px;
          padding: 12px;
          margin: 0 auto;
          border-radius: 50%;
          color: $color-gray600;
          background: $color-gray200;
        }

        .c-text-regular--s {
          color: $color-gray800;

          .icon-box {
            display: inline-flex;
            vertical-align: text-top;
          }
        }
      }
    }
    .follow-lists {
      margin: 0 auto;
    }
  }
}
</style>
